import Plugin from '@ckeditor/ckeditor5-core/src/plugin'
import EventInfo from '@ckeditor/ckeditor5-utils/src/eventinfo'
import bindTwoStepCaretToAttribute from '@ckeditor/ckeditor5-engine/src/utils/bindtwostepcarettoattribute'
import getLastTextLine from '@ckeditor/ckeditor5-typing/src/utils/getlasttextline'
import AttributeCommand from '@ckeditor/ckeditor5-basic-styles/src/attributecommand'
import Rect from '@ckeditor/ckeditor5-utils/src/dom/rect'

const CONTEXTED_LINK_MODEL = 'contextedLink'
const CONTEXTED_LINK_VIEW = 'data-contexted-link'
const CONTEXTED_LINK_AUTOCOMPLETE = 'contextedLinkAutocomplete'

function getNodePosition(editor, modelPosition) {
  try {
    const mapper = editor.editing.mapper
    const viewPosition = mapper.toViewPosition(modelPosition)
    const viewRange = editor.editing.view.createRange(viewPosition)
    const domConverter = editor.editing.view.domConverter
    const rangeRects = Rect.getDomRangeRects(domConverter.viewRangeToDom(viewRange)).pop()
    return rangeRects
  } catch (e) {
    console.log(e)
  }
}

export default class ContextedLink extends Plugin {
  init() {
    const editor = this.editor
    const model = editor.model
    editor.commands.add('autocomplete', new AttributeCommand(editor, CONTEXTED_LINK_AUTOCOMPLETE))
    model.schema.extend('$text', { allowAttributes: [CONTEXTED_LINK_MODEL, CONTEXTED_LINK_AUTOCOMPLETE] })
    editor.conversion.for('downcast').attributeToElement({
      model: CONTEXTED_LINK_MODEL,
      view: (value, writer) => {
        return writer.createAttributeElement('a', {
          [CONTEXTED_LINK_VIEW]: value,
          'class': 'contexted-link'
        }, { priority: 4 })
      }
    })

    editor.conversion.for('upcast').elementToAttribute({
      view: {
        name: 'a',
        key: CONTEXTED_LINK_VIEW
      },
      model: {
        key: CONTEXTED_LINK_MODEL,
        value: viewElement => viewElement.getAttribute(CONTEXTED_LINK_VIEW)
      }
    })

    const viewDocument = editor.editing.view.document
    viewDocument.on('keydown', (evt, data) => {
      const { keyCode } = data
      const keyCodes = [13, 38, 40] // Enter, Up, Down
      const selection = editor.model.document.selection
      if ((selection.hasAttribute(CONTEXTED_LINK_MODEL) || selection.hasAttribute(CONTEXTED_LINK_AUTOCOMPLETE)) && keyCodes.includes(keyCode)) {
        model.document.fire(new EventInfo('ContextedPlugin', 'contextedLinkAutocompleteKeypress'), { keyCode })
        data.preventDefault()
        evt.stop()
      }
    }, { priority: 'highest' })

    bindTwoStepCaretToAttribute({
      view: editor.editing.view,
      model: editor.model,
      emitter: this,
      attribute: CONTEXTED_LINK_MODEL,
      locale: editor.locale
    })
    this._setupLinkHighlight(CONTEXTED_LINK_MODEL)
  }

  afterInit() {
    const regexFormat = /(\[\[)([^[]+?)(\]\])$/g
    const editor = this.editor
    const setAutocomplete = ({ autocompleteText, position }) => {
      editor.model.document.fire(new EventInfo('ContextedPlugin', 'contextedLinkAutocompleteUpdate'), { autocompleteText, position })
    }
    editor.model.document.on('change', (evt, batch) => {
      if (batch.type === 'transparent') return
      const model = editor.model
      const selection = model.document.selection

      // Do nothing if selection is not collapsed.
      if (!selection.isCollapsed) return
      const changes = Array.from(model.document.differ.getChanges())
      const entry = changes[0]
      // Typing is represented by only a single change.
      // if (changes.length !== 1 || entry.type !== 'insert' || entry.name !== '$text' || entry.length !== 1) return
      if (changes.length !== 1 || (entry.type !== 'insert' && entry.type !== 'remove') || entry.name !== '$text' || entry.length !== 1) return
      const focus = selection.focus
      const block = focus.parent
      const { text, range } = getLastTextLine(model.createRange(model.createPositionAt(block, 0), focus), model)
      if (text.slice(-2) === '[[') this.editor.execute('autocomplete')

      const position = selection.getFirstPosition()
      const node = [position.textNode, position.nodeBefore, position.nodeAfter]
        .find(node => node && (node.hasAttribute(CONTEXTED_LINK_AUTOCOMPLETE) || node.hasAttribute(CONTEXTED_LINK_MODEL)))
      if (node && node.data) {
        const autocompleteText = node.data || ''
        const nodePosition = editor.model.createPositionFromPath(node.root, node.getPath())
        const event = { position: getNodePosition(editor, nodePosition), autocompleteText }
        setAutocomplete(event)
      } else {
        setAutocomplete({ autocompleteText: null, position: {} })
      }

      let result
      const format = []
      while ((result = regexFormat.exec(text)) !== null) {
        if (result && result.length < 4) {
          break
        }
        let { index, '1': leftDel, '2': content, '3': rightDel } = result
        // Real matched string - there might be some non-capturing groups so we need to recalculate starting index.
        const found = leftDel + content + rightDel
        index += result[0].length - found.length
        format.push([index + leftDel.length, index + leftDel.length + content.length])
      }
      const rangesToFormat = format.map(array => model.createRange(range.start.getShiftedBy(array[0]), range.start.getShiftedBy(array[1])))

      model.enqueueChange(writer => {
        const validRanges = editor.model.schema.getValidRanges(rangesToFormat, CONTEXTED_LINK_MODEL)
        for (const range of validRanges) {
          for (const item of range.getItems()) {
            if (item.data) {
              // this.editor.model.document.fire(new EventInfo('ContextedPlugin', 'createNote'), item.data)
              writer.setAttribute(CONTEXTED_LINK_MODEL, true, range)
              writer.removeSelectionAttribute(CONTEXTED_LINK_AUTOCOMPLETE)
              setAutocomplete({ autocompleteText: null, position: {} })
            }
          }
        }
      })
    })
  }

  _setupLinkHighlight(attr) {
    const HIGHLIGHT_CLASS = 'ck-link_selected'
    const editor = this.editor
    const view = editor.editing.view
    const highlightedLinks = new Set()

    // Adding the class.
    view.document.registerPostFixer(writer => {
      const selection = editor.model.document.selection
      if (selection.hasAttribute(attr)) {
        const modelRange = findLinkRange(selection.getFirstPosition(), selection.getAttribute(attr), editor.model, attr)
        const viewRange = editor.editing.mapper.toViewRange(modelRange)

        // There might be multiple `a` elements in the `viewRange`, for example, when the `a` element is
        // broken by a UIElement.
        for (const item of viewRange.getItems()) {
          if (item.is('a')) {
            writer.addClass(HIGHLIGHT_CLASS, item)
            highlightedLinks.add(item)
          }
        }
      }
    })

    // Removing the class.
    editor.conversion.for('editingDowncast').add(dispatcher => {
      // Make sure the highlight is removed on every possible event, before conversion is started.
      dispatcher.on('insert', removeHighlight, { priority: 'highest' })
      dispatcher.on('remove', removeHighlight, { priority: 'highest' })
      dispatcher.on('attribute', removeHighlight, { priority: 'highest' })
      dispatcher.on('selection', removeHighlight, { priority: 'highest' })

      function removeHighlight() {
        view.change(writer => {
          for (const item of highlightedLinks.values()) {
            writer.removeClass(HIGHLIGHT_CLASS, item)
            highlightedLinks.delete(item)
          }
        })
      }
    })
  }
}

function findLinkRange(position, value, model, attr) {
  return model.createRange(_findBound(position, value, true, model, attr), _findBound(position, value, false, model, attr))
}

// Walks forward or backward (depends on the `lookBack` flag), node by node, as long as they have the same `linkHref` attribute value
// and returns a position just before or after (depends on the `lookBack` flag) the last matched node.
//
// @param {module:engine/model/position~Position} position The start position.
// @param {String} value The `linkHref` attribute value.
// @param {Boolean} lookBack Whether the walk direction is forward (`false`) or backward (`true`).
// @returns {module:engine/model/position~Position} The position just before the last matched node.
function _findBound(position, value, lookBack, model, attr) {
  // Get node before or after position (depends on `lookBack` flag).
  // When position is inside text node then start searching from text node.
  let node = position.textNode || (lookBack ? position.nodeBefore : position.nodeAfter)

  let lastNode = null
  while (node && node.getAttribute(attr) === value) {
    lastNode = node
    node = lookBack ? node.previousSibling : node.nextSibling
  }

  return lastNode ? model.createPositionAt(lastNode, lookBack ? 'before' : 'after') : position
}
